/* eslint-disable no-restricted-syntax */

import type {GraphQlResponseError, ErrorSource} from '@sail/data';

import getErrorCode from '../../../shared/graphql/utils/getErrorCode';
import type {
  PersistedErrorInfo,
  MetaResponse,
  ErrorResponse,
  NormalizedErrorResponse,
} from '../../../shared/graphql/links/RestLink/types';

type LegacyError = GraphQlResponseError & NormalizedErrorResponse;

type LegacyTelemetry = {
  status?: number;
  url?: string;
  requestId?: string | null;
  body?: PersistedErrorInfo['body'];
};

const telemetryCache = new WeakMap<LegacyError, LegacyTelemetry>();

function createObjectWithHiddenProps<
  T extends {[key: string]: any},
  U extends {[key: string]: any},
>(shown: T, hidden: U): T & U {
  const result: Partial<T & U> = {};

  for (const key in hidden) {
    if (hidden.hasOwnProperty(key)) {
      let value = hidden[key];

      Object.defineProperty(result, key, {
        configurable: true,
        enumerable: true,

        get() {
          return value;
        },

        set(newValue) {
          value = newValue;
        },
      });
    }
  }

  for (const key in shown) {
    if (shown.hasOwnProperty(key)) {
      result[key] = shown[key];
    }
  }

  return result as T & U;
}

export default function rebuildErrorFromResponse(
  maybeBody: ErrorResponse | null,
  maybeMeta: MetaResponse,
) {
  const maybeError = maybeBody?.error || maybeBody;
  const maybeCode = (maybeError && getErrorCode(maybeError)) || null;
  const message = maybeError?.message ?? 'An unknown error has occurred.';
  const status = maybeMeta.status;

  // Create a base object that hides legacy properties. Legacy properties
  // are properties used by existing code but that will no longer exist
  // once V1 is migrated to GraphQL gateway.
  const legacyError: LegacyError = new Error(message);

  if (maybeError) {
    for (const key in maybeError) {
      if (maybeError.hasOwnProperty(key)) {
        legacyError[key] = maybeError[key];
      }
    }
  }

  legacyError.http_status = status;
  legacyError.request_id = maybeMeta.requestId;
  legacyError.url = maybeMeta.url;

  if (maybeBody?.two_factor_options) {
    legacyError.two_factor_options = maybeBody.two_factor_options;
  }

  if (maybeBody?.two_factor_primary_method) {
    legacyError.two_factor_primary_method = maybeBody.two_factor_primary_method;
  }

  let source: ErrorSource = '<unknown>';

  if (status <= 0) {
    source = 'network';
  }

  if (status >= 400 && status < 500) {
    source = 'client';
  }

  if (status >= 500 && status < 600) {
    source = legacyError.error_classification_for_frontend || 'server';
  }

  let code = 'could_not_get_error_code';

  if (status <= 0) {
    code = 'network_error';
  } else if (maybeCode) {
    code = maybeCode;
  }

  const fakeExtensions = {
    actionId: null,
    traceId: null,

    error: {
      code,
      source,
      status,
    },

    // These props come from the backend but they don't come in the
    // HTTP body. V2 link extracts them too and puts them in the error
    // body so that the observability link can read them.
    unstable_requestId: maybeMeta.requestId,
  };

  const extensions = createObjectWithHiddenProps(
    fakeExtensions,
    maybeError?.extensions || {},
  );

  const fakeGraphQlError: GraphQlResponseError = {
    message,
    locations: [{line: -1, column: -1}],
    path: [maybeMeta.field],
    extensions,
  };

  const finalError = createObjectWithHiddenProps(fakeGraphQlError, legacyError);

  const telemetry = {
    status,
    url: maybeMeta.url ? `${maybeMeta.url}` : '',
    requestId: maybeMeta.requestId,
    body: maybeBody,
  };

  telemetryCache.set(legacyError, telemetry);
  telemetryCache.set(finalError, telemetry);

  return finalError;
}
